import { Tour } from '../types';
import { supabase } from './supabaseService';
import { normalizeImageUrls } from '../utils/normalizeImageUrl';

// Database-first tour service with enhanced caching
class DatabaseTourService {
  private cache: Tour[] = [];
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 4 * 60 * 60 * 1000; // 4 hour cache for better performance
  private readonly STORAGE_KEY = 'tours_cache_v2';
  private readonly STORAGE_TIMESTAMP_KEY = 'tours_cache_timestamp_v2';
  private pendingRequest: Promise<Tour[]> | null = null; // Request deduplication
  private initialized = false; // Track if we've loaded initial cache

  // Load cache from localStorage
  private loadCacheFromStorage(): void {
    try {
      const cachedData = localStorage.getItem(this.STORAGE_KEY);
      const cachedTimestamp = localStorage.getItem(this.STORAGE_TIMESTAMP_KEY);

      if (cachedData && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp, 10);
        const now = Date.now();

        // Use localStorage cache if it's still valid
        if (now - timestamp < this.CACHE_DURATION) {
          this.cache = JSON.parse(cachedData);
          this.cacheTimestamp = timestamp;
           // console.log('📦 Loaded tours from localStorage cache');
        }
      }
    } catch (error) {
      console.error('Error loading cache from localStorage:', error);
    }
  }

  // Save cache to localStorage
  private saveCacheToStorage(tours: Tour[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tours));
      localStorage.setItem(this.STORAGE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Error saving cache to localStorage:', error);
    }
  }

  // Check if cache is valid
  private isCacheValid(): boolean {
    // First check in-memory cache
    if (this.cache.length > 0 && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION) {
      return true;
    }

    // Then check localStorage cache (but only if in-memory cache is empty)
    if (this.cache.length === 0) {
      this.loadCacheFromStorage();
      return this.cache.length > 0 && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION;
    }

    return false;
  }

  // Initialize cache from localStorage on service creation
  constructor() {
    // Load from localStorage immediately for instant response
    this.loadCacheFromStorage();
    if (this.cache.length > 0) {
      this.initialized = true;
    }
  }

  // Get all tours (database-first with enhanced caching)
  async getAllTours(forceRefresh: boolean = false): Promise<Tour[]> {
    // If force refresh is requested, bypass cache
    if (forceRefresh) {
       // console.log('🔄 Force refreshing tours from database...');
      this.clearCache();
      return await this.fetchToursFromDatabase();
    }

    // Return cached data immediately if valid (for instant response)
    if (this.isCacheValid() && this.cache.length > 0) {
      // Refresh in background if cache is older than 30 minutes
      const cacheAge = Date.now() - this.cacheTimestamp;
      if (cacheAge > 30 * 60 * 1000) {
        // Refresh in background without blocking
        this.fetchToursFromDatabase().catch(() => {
          // Silently fail - we have cache
        });
      }
      return [...this.cache]; // Return a copy to prevent mutations
    }

    // If cache exists but expired, return it immediately and refresh in background
    if (this.cache.length > 0) {
       // console.log('📦 Using expired cache, refreshing in background...');
      // Refresh in background without blocking
      this.fetchToursFromDatabase().catch(() => {
        // Silently fail - we have expired cache
      });
      return [...this.cache]; // Return expired cache immediately
    }

    // If cache is empty, try to load from localStorage first
    if (this.cache.length === 0) {
      this.loadCacheFromStorage();
      if (this.cache.length > 0) {
        // We have localStorage cache, return it immediately and refresh in background
         // console.log('📦 Using localStorage cache, refreshing in background...');
        this.fetchToursFromDatabase().catch(() => {
          // Silently fail - we have localStorage cache
        });
        return [...this.cache];
      }
    }

    // No cache at all - return empty array immediately and fetch in background
     // console.log('📡 No cache found, fetching tours from database in background...');

    if (this.pendingRequest) {
      // If there's already a pending request, return empty array immediately
      // The pending request will update cache when it completes
      return [];
    }

    // Create new request (non-blocking)
    this.pendingRequest = this.fetchToursFromDatabase();

    // Return empty array immediately, fetch will update cache in background
    this.pendingRequest
      .then(tours => {
        this.pendingRequest = null;
        // Cache will be updated by fetchToursFromDatabase
        // Dispatch event to notify components
        window.dispatchEvent(new Event('toursUpdated'));
      })
      .catch((error: any) => {
        this.pendingRequest = null;
        console.warn('⚠️ Background tour fetch failed:', error?.message || error);
      });

    return []; // Return empty array immediately
  }

  // Verify database connection (non-blocking)
  private async verifyDatabaseConnection(): Promise<void> {
    try {
      const { supabase } = await import('./supabaseService');
      const { data, error } = await supabase
        .from('tours')
        .select('id')
        .limit(1);

      if (error) {
        console.warn('⚠️ Database connection check failed:', error.message);
      } else {
         // console.log('✅ Database connection verified');
      }
    } catch (error) {
      console.warn('⚠️ Database connection check error:', error);
    }
  }

  // Internal method to fetch tours from database
  private async fetchToursFromDatabase(): Promise<Tour[]> {
    try {
       // console.log('🗄️ ========== FETCHING TOURS FROM DATABASE ==========');
       // console.log('🔍 Supabase client status:', supabase ? '✅ Initialized' : '❌ Not initialized');
       // console.log('🔍 Supabase URL:', process.env.REACT_APP_SUPABASE_URL || 'Using default');

      // Optional count query (non-blocking - runs in background, doesn't block data fetch)
      // This is just for logging/debugging purposes
      const countQueryPromise = (async () => {
        try {
          const countStartTime = Date.now();
          const countTimeout = new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error('Count query timeout')), 5000); // 5s timeout
          });

          const countPromise = supabase
            .from('tours')
            .select('*', { count: 'exact', head: true });

          const countResult = await Promise.race([countPromise, countTimeout]);
          const countDuration = Date.now() - countStartTime;
           // console.log(`✅ Count query successful in ${countDuration}ms - Total tours: ${countResult.count || 0}`);
          return countResult.count || 0;
        } catch (error: any) {
          // Silently fail - count query is optional, just log a warning
          if (error?.message?.includes('timeout')) {
            console.warn('⏰ Count query timeout (optional - proceeding with data fetch)');
          }
          return null;
        }
      })();

      // Don't wait for count query - proceed immediately with data fetch
       // console.log('🔍 Fetching tours from database...');
       // console.log('🔍 Query: SELECT * FROM tours ORDER BY created_at DESC');

      const queryStartTime = Date.now();

      // Add progress logging
      let progressInterval: NodeJS.Timeout | null = null;
      let queryTimeoutId: NodeJS.Timeout | null = null;
      const startProgressLogging = () => {
        let elapsed = 0;
        progressInterval = setInterval(() => {
          elapsed = Date.now() - queryStartTime;
          if (elapsed > 1000 && elapsed < 10000) {
             // console.log(`⏳ Full query still running... (${Math.floor(elapsed / 1000)}s elapsed)`);
          }
        }, 1000);
      };

      startProgressLogging();

      // Shorter timeout for full query (10 seconds)
      const queryPromise = supabase
        .from('tours')
        .select('*')
        .order('created_at', { ascending: false });

      const timeoutPromise = new Promise<never>((_, reject) => {
        queryTimeoutId = setTimeout(() => {
          if (progressInterval) clearInterval(progressInterval);
          reject(new Error('Query timeout: Full query took longer than 10 seconds. RLS may still be blocking.'));
        }, 10000); // 10 second timeout
      });

      // Race between query and timeout
      let queryResult: any;
      try {
         // console.log('⏳ Executing full query (10s timeout)...');
        queryResult = await Promise.race([queryPromise, timeoutPromise]);
        // Clear timeout if query succeeded
        if (queryTimeoutId) {
          clearTimeout(queryTimeoutId);
        }
        if (progressInterval) clearInterval(progressInterval);
      } catch (timeoutError: any) {
        // Clear timeout on error
        if (queryTimeoutId) {
          clearTimeout(queryTimeoutId);
        }
        if (progressInterval) clearInterval(progressInterval);
        if (timeoutError?.message?.includes('timeout')) {
          console.error('❌ ========== QUERY TIMEOUT ==========');
          console.error('The full query took longer than 10 seconds to complete.');
          console.error('Count query worked, but full query timed out.');
          console.error('This might indicate:');
          console.error('  1. RLS policies allow count but not full SELECT');
          console.error('  2. Large amount of data causing slow response');
          console.error('  3. Network connectivity issues');
          console.error('=====================================');
          console.error('💡 Try running fix_rls_explicit_anon.sql in Supabase SQL Editor');
          throw timeoutError;
        }
        throw timeoutError;
      }

      const { data, error } = queryResult;
      const queryDuration = Date.now() - queryStartTime;

       // console.log(`⏱️ Query completed in ${queryDuration}ms`);

      if (error) {
        console.error('❌ ========== SUPABASE QUERY ERROR ==========');
        console.error('Error code:', error.code || 'N/A');
        console.error('Error message:', error.message || 'N/A');
        console.error('Error details:', error.details || 'N/A');
        console.error('Error hint:', error.hint || 'N/A');
        console.error('Full error object:', JSON.stringify(error, null, 2));
        console.error('Query duration:', queryDuration, 'ms');
        console.error('=============================================');

        // Provide helpful error messages based on error code
        if (error.code === 'PGRST116') {
          console.error('💡 This error usually means the "tours" table does not exist.');
          console.error('   Solution: Create the tours table in your Supabase dashboard.');
        } else if (error.code === '42501') {
          console.error('💡 This error means Row Level Security (RLS) is blocking access.');
          console.error('   Solution: Run fix_rls_ultimate.sql in Supabase SQL Editor.');
          console.error('   This creates a simple, permissive SELECT policy that works for all roles.');
        } else if (error.code === '42P01') {
          console.error('💡 This error means the table does not exist.');
          console.error('   Solution: Create the tours table in your Supabase dashboard.');
        } else if (error.message?.includes('timeout')) {
          console.error('💡 Query timed out - database may be slow or unresponsive.');
          console.error('   Solution: Check your Supabase project status and network connection.');
        }

        throw error;
      }

      // Check if data is null or undefined
      if (data === null || data === undefined) {
        console.warn('⚠️ Query returned null/undefined data');
        console.warn('   This might indicate an RLS policy issue or table access problem');
        return [];
      }

       // console.log(`✅ Query successful! Duration: ${queryDuration}ms`);
       // console.log(`📊 Database returned ${data?.length || 0} tours`);
      if (data && data.length > 0) {
         // console.log('📋 Sample tour IDs:', data.slice(0, 5).map((t: any) => t.id));
      }
       // console.log('==================================================');

      // Convert database format to frontend format
      const tours: Tour[] = (data || []).map((dbTour: any) => ({
        id: dbTour.id,
        title: dbTour.title,
        description: dbTour.description,
        shortDescription: dbTour.short_description || dbTour.description?.substring(0, 150) + '...' || '',
        duration: dbTour.duration,
        price: dbTour.price || 0,
        currency: dbTour.currency || 'USD',
        difficulty: (dbTour.difficulty || 'Moderate') as 'Easy' | 'Moderate' | 'Challenging',
        category: dbTour.category || 'Safari',
        categoryId: dbTour.category_id || null,
        images: normalizeImageUrls(dbTour.images),
        highlights: dbTour.highlights || [],
        itinerary: dbTour.itinerary || [],
        includes: dbTour.includes || [],
        excludes: dbTour.excludes || [],
        requirements: dbTour.requirements || [],
        maxParticipants: dbTour.max_participants || 12,
        minParticipants: dbTour.min_participants || 2,
        rating: dbTour.rating || 0,
        reviewCount: dbTour.review_count || 0,
        location: dbTour.location || {
          name: 'Kenya',
          country: 'Kenya',
          region: 'East Africa',
          coordinates: {
            lat: -1.2921,
            lng: 36.8219
          },
          address: ''
        },
        guides: dbTour.guides || (dbTour.guide ? [dbTour.guide] : []),
        availability: dbTour.availability || [],
        active: dbTour.active !== false,
        tierVariants: dbTour.tier_variants || dbTour.tierVariants || null,
        createdAt: dbTour.created_at || new Date().toISOString(),
        updatedAt: dbTour.updated_at || new Date().toISOString()
      }));

      // Update cache (both in-memory and localStorage)
      this.cache = tours;
      this.cacheTimestamp = Date.now();
      this.saveCacheToStorage(tours);

       // console.log(`✅ Successfully loaded ${tours.length} tours from database and cached`);
      if (tours.length === 0) {
        console.warn('⚠️ No tours found in database. Make sure tours are added through the admin panel.');
      }
      return tours;

    } catch (error: any) {
      console.error('❌ ========== DATABASE FETCH ERROR ==========');
      console.error('Error type:', error?.constructor?.name || typeof error);
      console.error('Error message:', error?.message || 'Unknown error');
      console.error('Error code:', error?.code || 'N/A');
      console.error('Error details:', error?.details || 'N/A');
      console.error('Error hint:', error?.hint || 'N/A');
      console.error('Full error object:', error);
      console.error('Stack trace:', error?.stack || 'No stack trace');

      // Check if it's a timeout error
      if (error?.message?.includes('timeout') || error?.message?.includes('Timeout')) {
        console.error('🔍 This is a TIMEOUT error');
        console.error('   The database query took longer than 30 seconds');
        console.error('   Possible causes:');
        console.error('   - Row Level Security (RLS) policies blocking access (MOST LIKELY)');
        console.error('   - Database is slow or overloaded');
        console.error('   - Network connectivity issues');
        console.error('   - Large amount of data to fetch');
        console.error('');
        console.error('   💡 SOLUTION: Check RLS policies in Supabase dashboard');
        console.error('      Go to: Authentication > Policies > tours table');
        console.error('      Make sure there is a policy allowing SELECT for authenticated/anonymous users');
      }
      // Check if it's a Supabase error
      else if (error?.code) {
        console.error('🔍 This appears to be a Supabase error');
        console.error('   Common causes:');
        console.error('   - Table "tours" does not exist');
        console.error('   - Row Level Security (RLS) policies blocking access');
        console.error('   - Invalid Supabase credentials');
        console.error('   - Network connectivity issues');
      }
      // Check if it's a network error
      else if (error?.message?.includes('fetch') || error?.message?.includes('network')) {
        console.error('🔍 This appears to be a NETWORK error');
        console.error('   Possible causes:');
        console.error('   - No internet connection');
        console.error('   - Supabase service is down');
        console.error('   - Firewall blocking requests');
      }

      // If we have cached data (even expired), return it
      if (this.cache.length > 0) {
        console.warn('⚠️ Using expired cache due to database error');
        return [...this.cache];
      }

      // Try to load from localStorage even if expired
      this.loadCacheFromStorage();
      if (this.cache.length > 0) {
        console.warn('⚠️ Using expired localStorage cache due to database error');
        return [...this.cache];
      }

      // No cached data available
      console.error('❌ ========== NO TOURS AVAILABLE ==========');
      console.error('   - Database fetch failed');
      console.error('   - No cached data available');
      console.error('   - Returning empty array');
      console.error('===========================================');
      return [];
    }
  }

  // Get only active tours
  async getActiveTours(): Promise<Tour[]> {
    const allTours = await this.getAllTours();
    return allTours.filter(tour => tour.active);
  }

  // Get tour by ID
  async getTourById(id: string): Promise<Tour | null> {
    // Check cache first
    if (this.isCacheValid()) {
      const cachedTour = this.cache.find(tour => tour.id === id);
      if (cachedTour) {
         // console.log('📦 Returning cached tour:', id);
        return cachedTour;
      }
    }

    try {
       // console.log('🗄️ Fetching tour from database:', id);
      const { data, error } = await supabase
        .from('tours')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching tour from database:', error);
        return null;
      }

      if (!data) {
         // console.log('Tour not found in database:', id);
        return null;
      }

      // Convert database format to frontend format
      const tour: Tour = {
        id: data.id,
        title: data.title,
        description: data.description,
        shortDescription: data.short_description || data.description?.substring(0, 150) + '...' || '',
        duration: data.duration,
        price: data.price || 0,
        currency: data.currency || 'USD',
        difficulty: (data.difficulty || 'Moderate') as 'Easy' | 'Moderate' | 'Challenging',
        category: (data.category || 'Safari'),
        images: normalizeImageUrls(data.images),
        highlights: data.highlights || [],
        itinerary: data.itinerary || [],
        includes: data.includes || [],
        excludes: data.excludes || [],
        requirements: data.requirements || [],
        maxParticipants: data.max_participants || 12,
        minParticipants: data.min_participants || 2,
        rating: data.rating || 0,
        reviewCount: data.review_count || 0,
        location: data.location || {
          name: 'Kenya',
          country: 'Kenya',
          region: 'East Africa',
          coordinates: {
            lat: -1.2921,
            lng: 36.8219
          },
          address: ''
        },
        guides: data.guides || (data.guide ? [data.guide] : []),
        availability: data.availability || [],
        active: data.active !== false,
        tierVariants: data.tier_variants || data.tierVariants || null,
        createdAt: data.created_at || new Date().toISOString(),
        updatedAt: data.updated_at || new Date().toISOString()
      };

       // console.log('✅ Loaded tour from database:', id);
      return tour;

    } catch (error) {
      console.error('Database error fetching tour:', error);
      return null;
    }
  }

  // Add new tour
  async addTour(tourData: Omit<Tour, 'id' | 'createdAt' | 'updatedAt'>): Promise<Tour> {
    try {
       // console.log('🗄️ Adding new tour to database...');

      // Generate a proper UUID for the tour ID
      const tourId = crypto.randomUUID();

      // Convert frontend format to database format
      const dbTour = {
        id: tourId,
        title: tourData.title,
        description: tourData.description,
        short_description: tourData.shortDescription,
        duration: tourData.duration,
        price: tourData.price,
        currency: tourData.currency,
        difficulty: tourData.difficulty,
        category: tourData.category,
        images: tourData.images,
        highlights: tourData.highlights,
        itinerary: tourData.itinerary,
        includes: tourData.includes,
        excludes: tourData.excludes,
        requirements: tourData.requirements,
        max_participants: tourData.maxParticipants,
        min_participants: tourData.minParticipants,
        rating: tourData.rating,
        review_count: tourData.reviewCount,
        location: tourData.location,
        guides: tourData.guides,
        availability: tourData.availability,
        active: tourData.active
      };

      const { data, error } = await supabase
        .from('tours')
        .insert([dbTour])
        .select()
        .single();

      if (error) {
        console.error('Error adding tour to database:', error);
        console.error('Full error details:', JSON.stringify(error, null, 2));
        console.error('Tour data being inserted:', JSON.stringify(dbTour, null, 2));
        throw error;
      }

      // Convert back to frontend format
      const newTour: Tour = {
        id: data.id,
        title: data.title,
        description: data.description,
        shortDescription: data.short_description || data.description?.substring(0, 150) + '...' || '',
        duration: data.duration,
        price: data.price || 0,
        currency: data.currency || 'USD',
        difficulty: (data.difficulty || 'Moderate') as 'Easy' | 'Moderate' | 'Challenging',
        category: (data.category || 'Safari'),
        images: normalizeImageUrls(data.images),
        highlights: data.highlights || [],
        itinerary: data.itinerary || [],
        includes: data.includes || [],
        excludes: data.excludes || [],
        requirements: data.requirements || [],
        maxParticipants: data.max_participants || 12,
        minParticipants: data.min_participants || 2,
        rating: data.rating || 0,
        reviewCount: data.review_count || 0,
        location: data.location || {
          name: 'Kenya',
          country: 'Kenya',
          region: 'East Africa',
          coordinates: {
            lat: -1.2921,
            lng: 36.8219
          },
          address: ''
        },
        guides: data.guides || (data.guide ? [data.guide] : []),
        availability: data.availability || [],
        active: data.active !== false,
        tierVariants: data.tier_variants || data.tierVariants || null,
        createdAt: data.created_at || new Date().toISOString(),
        updatedAt: data.updated_at || new Date().toISOString()
      };

      // Clear cache to force refresh
      this.clearCache();

       // console.log('✅ Tour added to database:', newTour.id);
      return newTour;

    } catch (error) {
      console.error('Database error adding tour:', error);
      throw error;
    }
  }

  // Update existing tour
  async updateTour(id: string, tourData: Omit<Tour, 'id' | 'createdAt' | 'updatedAt'>): Promise<Tour | null> {
    try {
       // console.log('🗄️ Updating tour in database:', id);
       // console.log('Tour data to update:', tourData);

      // Convert frontend format to database format
      const dbTour = {
        title: tourData.title,
        description: tourData.description,
        short_description: tourData.shortDescription,
        duration: tourData.duration,
        price: tourData.price,
        currency: tourData.currency,
        difficulty: tourData.difficulty,
        category: tourData.category,
        images: tourData.images || [],
        highlights: tourData.highlights || [],
        itinerary: tourData.itinerary || [],
        includes: tourData.includes || [],
        excludes: tourData.excludes || [],
        requirements: tourData.requirements || [],
        max_participants: tourData.maxParticipants,
        min_participants: tourData.minParticipants,
        rating: tourData.rating,
        review_count: tourData.reviewCount,
        location: tourData.location || null,
        guides: tourData.guides || [],
        availability: tourData.availability || [],
        active: tourData.active,
        updated_at: new Date().toISOString()
      };

       // console.log('Database update payload:', dbTour);

      // Remove any undefined or null values that might cause issues
      const cleanDbTour = Object.fromEntries(
        Object.entries(dbTour).filter(([_, value]) => value !== undefined)
      );

       // console.log('Cleaned database update payload:', cleanDbTour);

      const { data, error } = await supabase
        .from('tours')
        .update(cleanDbTour)
        .eq('id', id)
        .select();

      if (error) {
        console.error('Error updating tour in database:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        console.error('Full error object:', JSON.stringify(error, null, 2));
        return null;
      }

      if (!data || data.length === 0) {
         // console.log('Tour not found for update:', id);
        return null;
      }

      // Get the first (and should be only) updated tour
      const updatedDbTour = data[0];

      // Convert back to frontend format
      const updatedTour: Tour = {
        id: updatedDbTour.id,
        title: updatedDbTour.title,
        description: updatedDbTour.description,
        shortDescription: updatedDbTour.short_description || updatedDbTour.description?.substring(0, 150) + '...' || '',
        duration: updatedDbTour.duration,
        price: updatedDbTour.price || 0,
        currency: updatedDbTour.currency || 'USD',
        difficulty: (updatedDbTour.difficulty || 'Moderate') as 'Easy' | 'Moderate' | 'Challenging',
        category: (updatedDbTour.category || 'Safari'),
        images: updatedDbTour.images || [],
        highlights: updatedDbTour.highlights || [],
        itinerary: updatedDbTour.itinerary || [],
        includes: updatedDbTour.includes || [],
        excludes: updatedDbTour.excludes || [],
        requirements: updatedDbTour.requirements || [],
        maxParticipants: updatedDbTour.max_participants || 12,
        minParticipants: updatedDbTour.min_participants || 2,
        rating: updatedDbTour.rating || 0,
        reviewCount: updatedDbTour.review_count || 0,
        location: updatedDbTour.location || {
          name: 'Kenya',
          country: 'Kenya',
          region: 'East Africa',
          coordinates: {
            lat: -1.2921,
            lng: 36.8219
          },
          address: ''
        },
        guides: updatedDbTour.guides || (updatedDbTour.guide ? [updatedDbTour.guide] : []),
        availability: updatedDbTour.availability || [],
        active: updatedDbTour.active !== false,
        tierVariants: updatedDbTour.tier_variants || updatedDbTour.tierVariants || null,
        createdAt: updatedDbTour.created_at || new Date().toISOString(),
        updatedAt: updatedDbTour.updated_at || new Date().toISOString()
      };

      // Clear cache to force refresh
      this.clearCache();

       // console.log('✅ Tour updated in database:', id);
      return updatedTour;

    } catch (error) {
      console.error('Database error updating tour:', error);
      return null;
    }
  }

  // Delete tour
  async deleteTour(id: string): Promise<boolean> {
    try {
       // console.log('🗄️ Deleting tour from database:', id);

      const { error } = await supabase
        .from('tours')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting tour from database:', error);
        return false;
      }

      // Clear cache to force refresh
      this.clearCache();

       // console.log('✅ Tour deleted from database:', id);
      return true;

    } catch (error) {
      console.error('Database error deleting tour:', error);
      return false;
    }
  }

  // Clear cache (useful for admin operations)
  clearCache(): void {
     // console.log('🗑️ Clearing tour cache');
    this.cache = [];
    this.cacheTimestamp = 0;
    // Also clear localStorage
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.STORAGE_TIMESTAMP_KEY);
    } catch (error) {
      console.error('Error clearing localStorage cache:', error);
    }
  }

  // Force refresh cache
  async refreshCache(): Promise<Tour[]> {
     // console.log('🔄 Force refreshing tour cache');
    this.clearCache();
    return await this.getAllTours();
  }

  // Get tour statistics
  async getTourStats(): Promise<{
    totalTours: number;
    activeTours: number;
    totalRevenue: number;
    avgRating: number;
  }> {
    try {
      const tours = await this.getAllTours();

      return {
        totalTours: tours.length,
        activeTours: tours.filter(tour => tour.active).length,
        totalRevenue: tours.reduce((sum, tour) => sum + (tour.price * tour.reviewCount), 0),
        avgRating: tours.length > 0 ? tours.reduce((sum, tour) => sum + tour.rating, 0) / tours.length : 0
      };
    } catch (error) {
      console.error('Error calculating tour stats:', error);
      return {
        totalTours: 0,
        activeTours: 0,
        totalRevenue: 0,
        avgRating: 0
      };
    }
  }
}

export const databaseTourService = new DatabaseTourService();
